import { APIModel } from "./api-model";
import { Donor } from "./donor";
import { AppliedTag } from "./match/applied-tag";
import { MatchCalculatedStats } from "./match/calculated-stats";
import { ChecklistApplication } from "./match/checklist/checklist-application";
import { Offer } from "./offer";
import { Organ } from "./organ";
import { TransplantCenter } from "./transplant-center";
import { formatDateTimeET } from "@/src/date-helpers";

export class Match extends APIModel {
  id!: string;
  code!: string;
  donor_id!: string;
  organ_id!: string;
  transplant_center_id!: string;
  offer_date!: string;
  donor!: Donor;
  organ!: Organ;
  transplant_center!: TransplantCenter;
  checklists!: ChecklistApplication[];
  tags!: AppliedTag[];
  calculated_stats!: MatchCalculatedStats;
  offers_ordered_by_seq!: Offer[];
  acceptancelist_page_visited!: string;
  acceptancelist_page_visited_at!: string;
  donoredit_page_visited!: string;
  donoredit_page_visited_at!: string;
  matchlist_page_visited!: boolean;
  matchlist_page_visited_at!: string;
  sequence_status_page_visited!: boolean;
  sequence_status_page_visited_at!: string;

  static fieldsToMap = {
    direct: [
      'id',
      'code',
      'donor_id',
      'organ_id',
      'transplant_center_id',
      'offer_date',
      'acceptancelist_page_visited',
      'acceptancelist_page_visited_at',
      'donoredit_page_visited',
      'donoredit_page_visited_at',
      'matchlist_page_visited',
      'matchlist_page_visited_at',
      'sequence_status_page_visited',
      'sequence_status_page_visited_at'
    ]
  }

  /**
   * Generates the breadcrumb text identifier for this match
   */
  get breadcrumbText(): string {
    return [this.donor?.code, this.code, this.transplant_center?.code, this.organ?.name].join(' - ');
  }

  get offerResponse(): string {
    const stats = this.calculated_stats;

    if (!stats) return 'None';
    if (stats.accepted) return 'Accepted';
    if (stats.declined) return 'Declined';
    if (stats.cross_clamp_time_passed) return 'Time passed';
    return 'None';
  }

  get offerResponseClass(): string {
    switch (this.offerResponse) {
      case 'Accepted': return 'badge-success';
      case 'Declined': return 'badge-danger';
      case 'Time passed': return 'badge-warning';
      default: return 'badge-info';
    }
  }

  get pageVisits(): { label: string, visited: boolean | string, visitedAt: string }[] {
    return [
      {
        label: 'Acceptance List',
        visited: this.acceptancelist_page_visited,
        visitedAt: this.acceptancelist_page_visited_at ? formatDateTimeET(this.acceptancelist_page_visited_at)+ ' ET' : 'Not visited'
      },
      {
        label: 'Donor Summary',
        visited: this.donoredit_page_visited,
        visitedAt: this.donoredit_page_visited_at ? formatDateTimeET(this.donoredit_page_visited_at)+ ' ET' : 'Not visited'
      },
      {
        label: 'Match List',
        visited: this.matchlist_page_visited,
        visitedAt: this.matchlist_page_visited_at ? formatDateTimeET(this.matchlist_page_visited_at)+ ' ET' : 'Not visited'
      },
      {
        label: 'Sequence Status',
        visited: this.sequence_status_page_visited,
        visitedAt: this.sequence_status_page_visited_at ? formatDateTimeET(this.sequence_status_page_visited_at)+ ' ET' : 'Not visited'
      }
    ];
  }

  get visitedOffers(): Offer[] {
    return (this.offers_ordered_by_seq || []).filter((offer: Offer) => offer.offer_details_page_visited);
  }

  static buildFromAPI(record: any): Match {
    const row = new Match();
    record = record || {};

    this.mapFieldsDirectly<Match>(Match.fieldsToMap.direct, record, row);
    row.donor = Donor.buildFromAPI(record.donor);
    row.organ = Organ.buildFromAPI(record.organ);
    row.transplant_center = TransplantCenter.buildFromAPI(record.transplant_center);
    row.checklists = [];
    if(record.checklists) {
      record.checklists.forEach((checklist: any) => {
        row.checklists.push(ChecklistApplication.buildFromAPI(checklist));
      })
    }

    row.tags = [];
    if(record.active_applied_tags) {
      record.active_applied_tags.forEach((tag: any) => {
        row.tags.push(AppliedTag.buildFromAPI(tag));
      })
    }

    row.calculated_stats = MatchCalculatedStats.buildFromAPI(record.calculated_stats);
    row.offers_ordered_by_seq = (record.offers_ordered_by_seq || []).map((offer: any) => Offer.buildFromAPI(offer));
    return row
  }
}